import {
  CEndReasonTypes,
  CFirstDayIdx,
  CScheduler,
  DEFAULT_SCHEDULERS_LIMIT,
  EDays,
  EEndReasonType,
  EMonths,
  ESchedulerPrefix,
  ESchedulerTypeTransform,
  EWeekDays,
  type IScheduler as IPkgScheduler,
  type TScheduler as TPkgScheduler,
  type TSchedulerDuration,
  type TSchedulerRange as TPkgSchedulerRange,
  type TSchedulerRepeat,
} from '@teamco/ischeduler-core/types';
import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';

import { EStatus, type IMetadata, type IObjectId } from './common.type';
import { CNsDiscount } from './price.type';

// ---------------------------------------------------------------------------
// Shared scheduler vocabulary — single source of truth in
// `@teamco/ischeduler-core`. The enums/constants/structural types are
// re-exported verbatim (identical values); the date-bearing types are
// SPECIALIZED to our domain (Firestore `Timestamp` persisted, `Dayjs` in forms)
// + our richer `IMetadata`. See docs/conventions.md "Scheduler types".
// ---------------------------------------------------------------------------
export {
  CEndReasonTypes,
  CFirstDayIdx,
  CScheduler,
  DEFAULT_SCHEDULERS_LIMIT,
  EDays,
  EEndReasonType,
  EMonths,
  ESchedulerPrefix,
  ESchedulerTypeTransform,
  EWeekDays,
};
export type { TSchedulerDuration, TSchedulerRepeat };

/** Our scheduler dates: Firestore `Timestamp` persisted, `Dayjs` in the UI. */
type TSchedulerDate = Timestamp | dayjs.Dayjs;

/** App-specific: which entity collections schedulers attach to (not in the package). */
export const CSchedulerEntityPaths = {
  FEATURES: 'features',
  SUBSCRIPTIONS: 'subscriptions',
} as const;

export type TSchedulerEntityPaths =
  (typeof CSchedulerEntityPaths)[keyof typeof CSchedulerEntityPaths];

/** Range specialized to our date representation. */
export type TSchedulerRange = TPkgSchedulerRange<TSchedulerDate>;

/**
 * Scheduler shape: inherits the package's structure (generic over our date type)
 * but keeps OUR required `id`/`status` and richer `metadata`, so existing
 * consumers are unchanged. `discount` is omitted on purpose — the base scheduler
 * carries no discount; it appears only on `TScheduler<P>` via the conditional
 * mapping (`null` for SALE, `TDiscount` otherwise), matching the original shape.
 */
export type IScheduler = Omit<
  IPkgScheduler<TSchedulerDate>,
  'id' | 'status' | 'metadata' | 'discount'
> & {
  id: IObjectId['id'];
  status: EStatus;
  metadata: IMetadata;
};

export type TScheduler<P extends ESchedulerPrefix> = Omit<
  TPkgScheduler<P, TSchedulerDate>,
  'id' | 'status' | 'metadata'
> & {
  id: IObjectId['id'];
  status: EStatus;
  metadata: IMetadata;
};

export type RequiredSchedulers = {
  [
    K in Exclude<ESchedulerPrefix, ESchedulerPrefix.TRIAL_DISCOUNT>
  ]: TScheduler<K>[];
};

export type OptionalSchedulers = {
  [ESchedulerPrefix.TRIAL_DISCOUNT]?: TScheduler<ESchedulerPrefix.TRIAL_DISCOUNT>[];
};

export type TSchedulers = {
  [CScheduler]: RequiredSchedulers & OptionalSchedulers;
};

const BASE_SCHEDULER: Omit<
  IScheduler,
  'id' | 'type' | 'metadata' | typeof CNsDiscount
> = {
  duration: {
    type: 'MONTH',
    period: 7,
  },
  repeat: {
    weekly: {
      days: [(Object.keys(EDays) as (keyof typeof EDays)[])[CFirstDayIdx]],
    },
    monthly: {
      weekDay: 'SECOND',
      monthDay: 2,
      type: 'PERIOD',
    },
    yearly: {
      months: ['JANUARY'],
    },
  },
  range: {
    startedAt: dayjs(new Date()),
    endReason: { type: CEndReasonTypes[2], expiredAt: null },
  },
  status: EStatus.PROCESSING,
};

export const DEFAULT_SALE_SCHEDULER: Partial<
  TScheduler<ESchedulerPrefix.SALE>
> = {
  ...BASE_SCHEDULER,
  type: ESchedulerPrefix.SALE,
  // Explicitly set discount to null for SALE type
  [CNsDiscount]: null,
  metadata: {
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  },
};

export const DEFAULT_DISCOUNT_SCHEDULER: Partial<
  TScheduler<ESchedulerPrefix.DISCOUNT>
> = {
  ...BASE_SCHEDULER,
  type: ESchedulerPrefix.DISCOUNT,
  // Add the discount object for DISCOUNT type
  [CNsDiscount]: {
    type: 'PERCENT',
    value: 1,
  },
  metadata: {
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  },
};
